import { API_URL } from '../env';

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** Sent as the Bearer token when set. */
  token?: string | null;
};

async function errorMessage(res: Response): Promise<string> {
  try {
    const body: { message?: unknown; error?: unknown } = await res.json();
    const text = body.message ?? body.error;
    if (typeof text === 'string' && text) {
      return text;
    }
  } catch {
    // The body was not JSON; the status line below is all there is.
  }
  return `Request failed (${res.status})`;
}

/** One JSON call to the API. Throws ApiError on a non-2xx status. */
export async function request<T>(
  path: string,
  { method = 'GET', body, token }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await errorMessage(res));
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}
